import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { Users, ArrowLeft, ShieldCheck, Clock, Trash2, Save, UserMinus, UserPlus, Mail, Shield, Plus, Check, Pencil, Calendar, X } from 'lucide-react';
import Modal from '../components/Modal';
import MonitoringDashboard from '../components/MonitoringDashboard';
import NewSensorModal, { SensorConfig } from '../components/NewSensorModal';
import AddDataResourceModal from '../components/AddDataResourceModal';
import ProjectDocuments from '../components/ProjectDocuments';
import CombinedVisualization from '../components/CombinedVisualization';
import { ProjectVisual, ProjectVisualPicker } from '../components/projectVisual';

const ProjectDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [project, setProject] = useState<any>(null);
  const [members, setMembers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const firstLoad = useRef(true);

  const [isEditing, setIsEditing] = useState(false);
  const [stagedForDeletion, setStagedForDeletion] = useState<string[]>([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState<any>({ name: '', description: '', data_inicio: '', data_fim: '', image: null });
  const [savingEdit, setSavingEdit] = useState(false);
  const [showNewSensorModal, setShowNewSensorModal] = useState(false);
  const [showAddDataResourceModal, setShowAddDataResourceModal] = useState(false);
  const [showSensorSuccess, setShowSensorSuccess] = useState(false);
  const [currentSensorConfig, setCurrentSensorConfig] = useState<SensorConfig | null>(null);
  const [appendDataset, setAppendDataset] = useState<any>(null);
  
  const [inviteEmail, setInviteEmail] = useState('');
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [inviteRole, setInviteRole] = useState('VISUALIZER');
  
  const [modalConfig, setModalConfig] = useState<any>({ isOpen: false, title: '', message: '', type: 'info', onConfirm: null });

  useEffect(() => { fetchProjectData(); }, [id]);

  useEffect(() => {
    if (inviteEmail.length >= 3) {
      const delayDebounceFn = setTimeout(() => fetchSuggestions(), 300);
      return () => clearTimeout(delayDebounceFn);
    } else { setSuggestions([]); }
  }, [inviteEmail]);

  const fetchSuggestions = async () => {
    try {
      const { data } = await api.get(`/users/search?q=${inviteEmail}`);
      setSuggestions(data);
    } catch (err) { setSuggestions([]); }
  };

  const fetchProjectData = async () => {
    // Só mostra o skeleton na 1.ª carga; nos refetches (após ações) mantém o conteúdo
    // para não perder a posição de scroll.
    if (firstLoad.current) setLoading(true);
    try {
      const [projRes, membersRes] = await Promise.all([
        api.get(`/projects/${id}`),
        api.get(`/projects/${id}/members`)
      ]);
      setProject(projRes.data);
      setMembers(membersRes.data);
    } catch (err) {
      setModalConfig({ isOpen: true, title: 'Erro', message: 'Projeto inexistente.', type: 'danger', onConfirm: () => navigate('/dashboard') });
    } finally { setLoading(false); firstLoad.current = false; }
  };

  const handleNewSensorSuccess = (config: SensorConfig) => {
    setCurrentSensorConfig(config);
    setShowAddDataResourceModal(true);
  };

  const handleAppendData = (dataset: any) => {
    setAppendDataset({ id: dataset.id, label: dataset.label, metadata: dataset.metadata });
  };

  const handleDataResourceSuccess = (result?: { added?: number; append?: boolean }) => {
    setShowAddDataResourceModal(false);
    setAppendDataset(null);
    fetchProjectData();
    if (result?.append) {
      const n = result.added ?? 0;
      setModalConfig({
        isOpen: true,
        title: 'Dados adicionados',
        message: n > 0
          ? `${n} novo(s) registo(s) adicionado(s) ao sensor.`
          : 'Nenhum registo novo: todos os dados eram anteriores ou iguais ao último já existente.',
        type: n > 0 ? 'success' : 'info',
      });
    } else {
      setShowSensorSuccess(true);
    }
  };

  const handleDeleteDataset = (datasetId: string) => {
    setModalConfig({
        isOpen: true,
        title: 'Apagar Dados',
        message: 'Tens a certeza que desejas apagar permanentemente este conjunto de dados?',
        type: 'danger',
        onConfirm: async () => {
            try {
                await api.delete(`/projects/${id}/datasets/${datasetId}`);
                fetchProjectData();
                setModalConfig({ isOpen: true, title: 'Sucesso', message: 'Dados apagados.', type: 'success' });
            } catch (err) {
                setModalConfig({ isOpen: true, title: 'Erro', message: 'Erro ao apagar dados.', type: 'danger' });
            }
        }
    });
  };

  const toggleStageDeletion = (userId: string) => {
    setStagedForDeletion(prev => prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]);
  };

  const handleSaveAccessChanges = async () => {
    if (stagedForDeletion.length === 0) { setIsEditing(false); return; }
    setModalConfig({
      isOpen: true,
      title: 'Confirmar Remoção',
      message: `Desejas remover permanentemente ${stagedForDeletion.length} membro(s)?`,
      type: 'danger',
      onConfirm: async () => {
        try {
          await Promise.all(stagedForDeletion.map(userId => api.delete(`/projects/${id}/members/${userId}`)));
          setStagedForDeletion([]); setIsEditing(false); fetchProjectData();
          setModalConfig({ isOpen: true, title: 'Sucesso', message: 'Equipa atualizada.', type: 'success' });
        } catch (err) { setModalConfig({ isOpen: true, title: 'Erro', message: 'Não foi possível atualizar a equipa.', type: 'danger' }); }
      }
    });
  };

  const handleSendInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post(`/projects/${id}/invite?user_email=${inviteEmail}&access_level=${inviteRole}`);
      setShowInviteModal(false); setInviteEmail(''); fetchProjectData();
      setModalConfig({ isOpen: true, title: 'Sucesso', message: 'Convite enviado.', type: 'success' });
    } catch (err: any) {
      setModalConfig({ isOpen: true, title: 'Erro', message: err.response?.data?.detail || 'Erro ao convidar.', type: 'danger' });
    }
  };

  const openEditModal = () => {
    setEditForm({
      name: project?.name || '',
      description: project?.description || '',
      data_inicio: project?.data_inicio || '',
      data_fim: project?.data_fim || '',
      image: project?.image || null,
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingEdit(true);
    try {
      await api.patch(`/projects/${id}`, {
        name: editForm.name,
        description: editForm.description,
        data_inicio: editForm.data_inicio || null,
        data_fim: editForm.data_fim || null,
        image: editForm.image || null,
      });
      setShowEditModal(false);
      fetchProjectData();
      setModalConfig({ isOpen: true, title: 'Sucesso', message: 'Projeto atualizado.', type: 'success' });
    } catch (err: any) {
      setModalConfig({ isOpen: true, title: 'Erro', message: err.response?.data?.detail || 'Erro ao atualizar o projeto.', type: 'danger' });
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeleteProject = () => {
    setShowEditModal(false);
    setModalConfig({
      isOpen: true,
      title: 'Apagar projeto',
      message: 'Tens a certeza que queres apagar este projeto permanentemente? Esta ação não pode ser desfeita.',
      type: 'danger',
      onConfirm: async () => {
        try {
          await api.delete(`/projects/${id}`);
          navigate('/dashboard');
        } catch (err) {
          setModalConfig({ isOpen: true, title: 'Erro', message: 'Não foi possível apagar o projeto.', type: 'danger' });
        }
      },
    });
  };

  const formatDate = (d?: string) => {
    if (!d) return null;
    try { return new Date(d).toLocaleDateString('pt-PT', { year: 'numeric', month: 'long', day: 'numeric' }); }
    catch { return d; }
  };

  // Permissões com base no papel DESTE projeto (não apenas no papel global):
  // um VISUALIZER do projeto só visualiza, mesmo que tenha papel global superior.
  const isSuperOrGeneral = ['SUPER_ADMIN', 'GENERAL_ADMIN'].includes(user?.global_role);
  const myProjectRole = members.find((m) => m.user_id === user?.id)?.project_role;
  const canManage = isSuperOrGeneral || ['SUPER_ADMIN', 'GENERAL_ADMIN', 'PROJECT_ADMIN'].includes(myProjectRole);
  const isProjectAdmin = canManage;

  if (loading) return (
    <div className="max-w-7xl mx-auto space-y-8 py-6 animate-pulse">
      <div className="h-4 w-32 bg-slate-200 dark:bg-slate-800 rounded-lg"></div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 h-[600px] bg-white dark:bg-slate-800 rounded-3xl border border-slate-100 dark:border-slate-800"></div>
        <div className="h-[600px] bg-white dark:bg-slate-800 rounded-3xl border border-slate-100 dark:border-slate-800"></div>
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto space-y-8 py-6 pb-20 overflow-hidden transition-colors duration-300">
      <Modal {...modalConfig} onClose={() => setModalConfig({ ...modalConfig, isOpen: false })} cancelText="Fechar" />
      
      <NewSensorModal 
        projectId={id!}
        isOpen={showNewSensorModal}
        onClose={() => setShowNewSensorModal(false)}
        onSuccess={handleNewSensorSuccess}
      />

      <AddDataResourceModal
        projectId={id!}
        sensorConfig={appendDataset ? null : currentSensorConfig}
        existingDataset={appendDataset}
        isOpen={showAddDataResourceModal || !!appendDataset}
        onClose={() => { setShowAddDataResourceModal(false); setAppendDataset(null); }}
        onSuccess={handleDataResourceSuccess}
      />

      {showSensorSuccess && (
        <div className="fixed inset-0 z-[2500] flex items-center justify-center p-4 bg-[#05070a]/90 backdrop-blur-sm transition-all animate-in fade-in duration-300">
          <div className="bg-white dark:bg-slate-800 rounded-3xl border border-slate-200 dark:border-slate-700 max-w-sm w-full overflow-hidden shadow-2xl animate-in zoom-in duration-300 transition-colors p-8 text-center">
            <div className="flex justify-center mb-5">
              <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 text-emerald-500 flex items-center justify-center">
                <Check size={32} />
              </div>
            </div>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white tracking-tight mb-2">Sensor adicionado</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">O sensor foi configurado e os dados carregados com sucesso.</p>

            <button
              onClick={() => setShowSensorSuccess(false)}
              className="w-full py-3 bg-blue-600 text-white rounded-xl font-semibold text-sm hover:bg-blue-700 transition-all active:scale-[0.98]"
            >
              Concluir
            </button>
          </div>
        </div>
      )}

      {showInviteModal && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-[#05070a]/90 transition-all">
          <div className="bg-white dark:bg-slate-800 rounded-3xl border border-slate-200 dark:border-slate-700 max-w-md w-full overflow-hidden animate-in zoom-in duration-300 transition-colors shadow-2xl">
            <div className="p-8">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white tracking-tight mb-1">Convidar membro</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">Adiciona um utilizador a este projeto.</p>
                <form onSubmit={handleSendInvite} className="space-y-4">
                <div className="relative text-left">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                    <input type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} className="w-full pl-11 pr-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl outline-none focus:ring-4 focus:ring-blue-500/10 font-medium text-slate-700 dark:text-white transition-all placeholder:text-slate-400 placeholder:font-normal" placeholder="Email do utilizador" required />
                    {suggestions.length > 0 && (
                    <div className="absolute z-[1100] w-full mt-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-2xl overflow-hidden max-h-48 overflow-y-auto">
                        {suggestions.map((u, idx) => (
                        <div key={idx} onClick={() => { setInviteEmail(u.email); setSuggestions([]); }} className="p-3 hover:bg-blue-50 dark:hover:bg-slate-700 cursor-pointer border-b border-slate-50 dark:border-slate-700 last:border-0 flex items-center space-x-3 transition-colors">
                            {u.profile_pic_url ? (
                                <img src={u.profile_pic_url} className="w-8 h-8 rounded-lg object-cover shrink-0" alt="S" />
                            ) : (
                                <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center text-white text-xs font-semibold uppercase shrink-0">{u.email[0]}</div>
                            )}
                            <div className="min-w-0 text-left">
                                <p className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">{u.full_name || u.email}</p>
                                {u.full_name && <p className="text-xs text-slate-400 truncate">{u.email}</p>}
                            </div>
                        </div>
                        ))}
                    </div>
                    )}
                </div>
                <div className="relative text-left">
                    <Shield className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                    <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value)} className="w-full pl-11 pr-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl outline-none focus:ring-4 focus:ring-blue-500/10 font-medium text-slate-700 dark:text-white appearance-none cursor-pointer">
                        <option value="VISUALIZER">Visualizador</option>
                        <option value="PROJECT_ADMIN">Administrador do projeto</option>
                    </select>
                </div>
                <div className="flex items-center justify-end gap-3 pt-1">
                  <button type="button" onClick={() => setShowInviteModal(false)} className="px-5 py-2.5 text-sm font-medium text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-colors">Cancelar</button>
                  <button type="submit" className="px-6 py-2.5 bg-slate-900 dark:bg-blue-600 text-white font-semibold text-sm rounded-xl hover:bg-black dark:hover:bg-blue-700 transition-all">Enviar convite</button>
                </div>
                </form>
            </div>
          </div>
        </div>
      )}

      {showEditModal && (
        <div className="fixed inset-0 z-[1500] flex items-center justify-center p-4 bg-[#05070a]/90 backdrop-blur-sm transition-all animate-in fade-in duration-300">
          <div className="bg-white dark:bg-[#161b26] rounded-3xl border border-slate-200 dark:border-slate-800 max-w-lg w-full overflow-hidden shadow-2xl animate-in zoom-in duration-300 transition-colors">
            <div className="flex items-center justify-between p-6 border-b border-slate-100 dark:border-slate-800">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white tracking-tight">Editar projeto</h3>
              <button onClick={() => setShowEditModal(false)} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors"><X size={20} className="text-slate-400" /></button>
            </div>
            <form onSubmit={handleSaveEdit} className="p-6 space-y-5">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Nome</label>
                <input type="text" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} required className="w-full p-3.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none focus:ring-4 focus:ring-blue-500/10 transition-all font-bold text-slate-700 dark:text-white" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Descrição</label>
                <textarea value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} className="w-full p-3.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none focus:ring-4 focus:ring-blue-500/10 transition-all min-h-[100px] leading-relaxed text-slate-600 dark:text-slate-300 font-medium" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Início</label>
                  <input type="date" value={editForm.data_inicio} onChange={(e) => setEditForm({ ...editForm, data_inicio: e.target.value })} className="w-full p-3.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none focus:ring-4 focus:ring-blue-500/10 transition-all font-bold text-slate-700 dark:text-white" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Prevista de fim</label>
                  <input type="date" value={editForm.data_fim} min={editForm.data_inicio || undefined} onChange={(e) => setEditForm({ ...editForm, data_fim: e.target.value })} className="w-full p-3.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none focus:ring-4 focus:ring-blue-500/10 transition-all font-bold text-slate-700 dark:text-white" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Imagem ou ícone</label>
                <ProjectVisualPicker
                  value={editForm.image}
                  onChange={(v) => setEditForm({ ...editForm, image: v })}
                  onError={(message) => setModalConfig({ isOpen: true, title: 'Erro', message, type: 'danger' })}
                />
              </div>
              <div className="flex items-center justify-between gap-3 pt-2">
                <button type="button" onClick={handleDeleteProject} className="inline-flex items-center gap-2 px-4 py-3 text-sm font-medium text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors">
                  <Trash2 size={16} /> <span>Apagar projeto</span>
                </button>
                <div className="flex items-center gap-3">
                  <button type="button" onClick={() => setShowEditModal(false)} className="px-5 py-3 text-sm font-medium text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-colors">Cancelar</button>
                  <button type="submit" disabled={savingEdit} className="inline-flex items-center gap-2 px-6 py-3 bg-slate-900 dark:bg-blue-600 text-white rounded-xl font-semibold text-sm hover:bg-black dark:hover:bg-blue-700 transition-all disabled:opacity-60">
                    <Save size={16} /> <span>{savingEdit ? 'A guardar…' : 'Guardar'}</span>
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 text-slate-400 hover:text-blue-600 transition-colors text-sm font-medium mb-4">
        <ArrowLeft size={16} /> <span>Voltar ao dashboard</span>
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch">
        <div className="lg:col-span-2">
          <div className="bg-white dark:bg-slate-800 p-8 rounded-3xl border border-slate-200 dark:border-slate-800 h-full min-h-[600px] flex flex-col transition-colors">
            <div className="flex items-center gap-5 mb-8 shrink-0 border-b border-slate-100 dark:border-slate-700/50 pb-6">
              <div className="w-16 h-16 bg-blue-600 rounded-2xl overflow-hidden flex items-center justify-center text-white shrink-0"><ProjectVisual image={project?.image} size={32} /></div>
              <div className="flex-1 min-w-0">
                <h2 className="text-2xl font-semibold text-slate-900 dark:text-white tracking-tight truncate">{project?.name}</h2>
                <p className="text-slate-400 dark:text-slate-500 text-xs mt-1">ID: {id?.slice(0,8)}</p>
              </div>
              {canManage && (
                <button onClick={openEditModal} className="p-2.5 text-slate-400 hover:text-blue-600 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-700 transition-all shrink-0" title="Editar projeto">
                  <Pencil size={18} />
                </button>
              )}
            </div>
            <div className="flex-1 space-y-5 text-slate-600 dark:text-slate-300 leading-relaxed">
              <h4 className="text-sm font-semibold text-slate-900 dark:text-white">Sobre</h4>
              <p className="text-base">{project?.description || "Sem descrição."}</p>
              {(project?.data_inicio || project?.data_fim) && (
                <div className="flex flex-wrap gap-3 pt-2">
                  {project?.data_inicio && (
                    <div className="flex items-center gap-2 px-3.5 py-2 bg-slate-50 dark:bg-slate-900/50 rounded-xl border border-slate-100 dark:border-slate-800">
                      <Calendar size={14} className="text-emerald-500" />
                      <span className="text-xs font-medium text-slate-500 dark:text-slate-400">Início:</span>
                      <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">{formatDate(project.data_inicio)}</span>
                    </div>
                  )}
                  {project?.data_fim && (
                    <div className="flex items-center gap-2 px-3.5 py-2 bg-slate-50 dark:bg-slate-900/50 rounded-xl border border-slate-100 dark:border-slate-800">
                      <Calendar size={14} className="text-blue-500" />
                      <span className="text-xs font-medium text-slate-500 dark:text-slate-400">Fim previsto:</span>
                      <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">{formatDate(project.data_fim)}</span>
                    </div>
                  )}
                </div>
              )}

              <div className="pt-4">
                <ProjectDocuments
                  show="documents"
                  projectId={id!}
                  documentos={project?.documentos || []}
                  ifcFile={project?.ifc_file || null}
                  canManage={canManage}
                  onChange={fetchProjectData}
                  onError={(message) => setModalConfig({ isOpen: true, title: 'Erro', message, type: 'danger' })}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="h-full">
          <div className={`p-8 rounded-3xl border h-full min-h-[600px] flex flex-col transition-all duration-300 shadow-sm ${isEditing ? 'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-900/30' : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700'}`}>
            <div className="flex items-center justify-between mb-8 border-b pb-6 border-slate-100 dark:border-slate-700 shrink-0">
              <div className={`flex items-center space-x-3 ${isEditing ? 'text-red-700 dark:text-red-400' : 'text-slate-900 dark:text-white'}`}>
                <div className={`p-2 rounded-xl ${isEditing ? 'bg-red-100 dark:bg-red-900/40' : 'bg-blue-600/10 text-blue-600'}`}>
                   {isEditing ? <UserMinus size={20}/> : <Users size={20} />}
                </div>
                <h3 className="text-sm font-semibold">{isEditing ? 'Gestão da equipa' : 'Equipa'}</h3>
              </div>
              <div className="flex items-center space-x-2">
                {isProjectAdmin && !isEditing && (
                  <button onClick={() => setShowInviteModal(true)} className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all active:scale-90" title="Convidar"><UserPlus size={16} /></button>
                )}
                <span className={`text-xs px-2.5 py-0.5 rounded-full font-semibold ${isEditing ? 'bg-red-200 dark:bg-red-900 text-red-700 dark:text-red-200' : 'bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-300'}`}>{members.length}</span>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto pr-2 space-y-4 custom-scrollbar">
              {members.map((member) => {
                const isStaged = stagedForDeletion.includes(member.user_id);
                return (
                  <div key={member.user_id} className={`flex items-center justify-between p-3 rounded-2xl border transition-all ${isStaged ? 'bg-red-100 dark:bg-red-950/40 border-red-300 dark:border-red-800 opacity-60' : isEditing ? 'bg-white dark:bg-slate-900 border-red-100 dark:border-red-900/20 cursor-pointer' : 'bg-slate-50 dark:bg-slate-900/50 border-slate-100 dark:border-slate-800'}`}>
                    <div
                        onClick={() => !isEditing && navigate(`/user/${member.user_id}`)}
                        className={`flex items-center gap-3 overflow-hidden min-w-0 ${!isEditing ? 'cursor-pointer' : ''}`}
                    >
                      {member.profile_pic_url ? (
                        <img src={member.profile_pic_url} alt="Avatar" className="w-11 h-11 shrink-0 rounded-xl object-cover border border-slate-200 dark:border-slate-700" />
                      ) : (
                        <div className={`w-11 h-11 shrink-0 rounded-xl flex items-center justify-center font-semibold transition-colors ${isStaged ? 'bg-red-500 text-white' : 'bg-blue-600 text-white'}`}>
                            {member.email[0].toUpperCase()}
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className={`text-sm font-medium truncate ${isStaged ? 'text-red-800 dark:text-red-300 line-through' : 'text-slate-800 dark:text-slate-200'}`}>
                            {member.full_name || 'Sem nome'}
                        </p>
                        <p className="text-xs text-blue-500 dark:text-blue-400 truncate">{member.project_role}</p>
                      </div>
                    </div>
                    <div className="shrink-0 ml-2">
                      {isEditing ? (
                        <div onClick={() => toggleStageDeletion(member.user_id)} className={`p-2 rounded-lg cursor-pointer ${isStaged ? 'bg-red-600 text-white' : 'text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'}`}>{isStaged ? <Save size={16} /> : <Trash2 size={16} />}</div>
                      ) : (
                        <div className="p-1.5">{member.status === 'ACCEPTED' ? <ShieldCheck size={18} className="text-emerald-500" /> : <Clock size={18} className="text-amber-500" />}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-8 space-y-3 shrink-0">
              {(isSuperOrGeneral || isProjectAdmin) && (
                <>
                  <button onClick={isEditing ? handleSaveAccessChanges : () => setIsEditing(true)} className={`w-full py-3.5 rounded-xl font-semibold text-sm transition-all flex items-center justify-center gap-2 active:scale-95 ${isEditing ? 'bg-red-600 dark:bg-red-700 text-white' : 'bg-slate-900 dark:bg-slate-700 text-white'}`}>
                      {isEditing ? <Save size={16} /> : <Trash2 size={16} />}
                      <span>{isEditing ? `Confirmar (${stagedForDeletion.length})` : 'Gerir acessos'}</span>
                  </button>
                  {isEditing && (
                    <button onClick={() => { setIsEditing(false); setStagedForDeletion([]); }} className="w-full py-3.5 bg-white dark:bg-slate-800 text-red-500 rounded-xl font-semibold text-sm border border-red-100 dark:border-red-900/20"><span>Cancelar</span></button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        <div className="lg:col-span-3">
            <MonitoringDashboard
                datasets={project?.datasets || []}
                onDeleteDataset={handleDeleteDataset}
                canDelete={isSuperOrGeneral || isProjectAdmin}
                onAppendData={handleAppendData}
                projectId={id}
                hasIfc={!!project?.ifc_file}
                onDatasetUpdated={fetchProjectData}
                eventos={project?.eventos || []}
                gravacoes={project?.gravacoes || []}
            />
        </div>
        <div className="flex flex-col space-y-6">
            <ProjectDocuments
                show="ifc"
                projectId={id!}
                documentos={project?.documentos || []}
                ifcFile={project?.ifc_file || null}
                canManage={canManage}
                onChange={fetchProjectData}
                onError={(message) => setModalConfig({ isOpen: true, title: 'Erro', message, type: 'danger' })}
            />

            {canManage && (
              <div
                  className="bg-white dark:bg-slate-800 p-8 rounded-3xl border border-slate-200 dark:border-slate-800 flex flex-col justify-center items-center text-center group hover:border-emerald-500/50 transition-all cursor-pointer shadow-sm"
                  onClick={() => setShowNewSensorModal(true)}
              >
                  <div className="w-14 h-14 bg-emerald-500 rounded-2xl flex items-center justify-center text-white mb-4 group-hover:scale-105 transition-transform">
                      <Plus size={28} />
                  </div>
                  <h4 className="text-base font-semibold text-slate-900 dark:text-white mb-1">Novo sensor</h4>
                  <p className="text-slate-400 text-sm px-2 leading-relaxed">Configura um novo sensor para monitorização</p>
              </div>
            )}
        </div>
      </div>

      <CombinedVisualization
        projectId={id!}
        datasets={project?.datasets || []}
        views={project?.visualizacoes || []}
        eventos={project?.eventos || []}
        canManage={canManage}
        onChange={fetchProjectData}
        onError={(message) => setModalConfig({ isOpen: true, title: 'Erro', message, type: 'danger' })}
      />
    </div>
  );
};

export default ProjectDetail;
